/**
 * Categorization Rule Type Definitions (WP-CAT-12)
 *
 * Matches backend schemas from:
 * - services/core/app/schemas/categorization_rule.py
 * - services/core/app/models/categorization_rule.py (PatternType enum)
 */

// ============================================================================
// Pattern Type Enum
// ============================================================================

/**
 * Pattern type determines which field the rule pattern matches against.
 *
 * Source: services/core/app/models/categorization_rule.py PatternType enum
 */
export type PatternType = 'merchant_name' | 'description' | 'provider_category';

/**
 * Display configuration for pattern types.
 */
export const PATTERN_TYPE_CONFIG: Record<
  PatternType,
  { label: string; icon: string; description: string }
> = {
  merchant_name: {
    label: 'Merchant',
    icon: 'building-2',
    description: 'Match against merchant name field',
  },
  description: {
    label: 'Description',
    icon: 'file-text',
    description: 'Match against transaction name/description',
  },
  provider_category: {
    label: 'Provider',
    icon: 'tag',
    description: 'Match against Plaid/bank category code',
  },
};

// ============================================================================
// Categorization Rule Types
// ============================================================================

/**
 * Categorization rule from API response.
 *
 * Matches backend CategorizationRuleResponse schema.
 * Source: services/core/app/schemas/categorization_rule.py lines 212-242
 */
export interface CategorizationRule {
  id: string;
  name: string;
  pattern: string;
  patternType: PatternType;
  categoryId: string;
  priority: number;
  confidence: number;
  isActive: boolean;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Paginated list response for categorization rules.
 *
 * Matches backend CategorizationRuleListResponse schema.
 * Source: services/core/app/schemas/categorization_rule.py lines 245-254
 */
export interface CategorizationRuleListResponse {
  rules: CategorizationRule[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================================================
// Request Types
// ============================================================================

/**
 * Request payload for creating a new categorization rule.
 *
 * Matches backend CategorizationRuleCreate schema.
 * Source: services/core/app/schemas/categorization_rule.py lines 106-126
 */
export interface RuleCreateRequest {
  name: string;
  pattern: string;
  patternType: PatternType;
  categoryId: string;
  priority?: number; // default: 100
  confidence?: number; // default: 0.8
  isActive?: boolean; // default: true
}

/**
 * Request payload for updating an existing categorization rule.
 *
 * Matches backend CategorizationRuleUpdate schema.
 * Source: services/core/app/schemas/categorization_rule.py lines 129-168
 */
export interface RuleUpdateRequest {
  name?: string;
  pattern?: string;
  patternType?: PatternType;
  categoryId?: string;
  priority?: number;
  confidence?: number;
  isActive?: boolean;
}

/**
 * Request payload for testing rules against sample text.
 *
 * Matches backend CategorizationRuleTestRequest schema.
 * Source: services/core/app/schemas/categorization_rule.py lines 256-277
 */
export interface RuleTestRequest {
  text: string;
  patternType: PatternType;
}

// ============================================================================
// Response Types
// ============================================================================

/**
 * A single rule match result from testing.
 *
 * Matches backend CategorizationRuleTestMatch schema.
 * Source: services/core/app/schemas/categorization_rule.py lines 280-289
 */
export interface RuleTestMatch {
  ruleId: string;
  ruleName: string;
  categoryId: string;
  categoryName: string | null;
  confidence: number;
  priority: number;
  isSystem: boolean;
}

/**
 * Response from testing rules against sample text.
 *
 * Matches backend CategorizationRuleTestResponse schema.
 * Source: services/core/app/schemas/categorization_rule.py lines 292-303
 */
export interface RuleTestResponse {
  text: string;
  patternType: PatternType;
  matches: RuleTestMatch[];
  bestMatch: RuleTestMatch | null;
}

// ============================================================================
// API List Parameters
// ============================================================================

/**
 * Query parameters for listing categorization rules.
 */
export interface RuleListParams {
  patternType?: PatternType;
  isActive?: boolean;
  limit?: number;
  offset?: number;
}
